import { useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Search,
  Users,
} from "lucide-react";

import { Badge, BadgeColor } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import { useLeads } from "../hooks/useLeads";
import { Lead, LeadStatus } from "../types/leads";

// --- Status pills ---

const STATUS_BADGE: Record<LeadStatus, { label: string; color: BadgeColor }> = {
  new: { label: "New", color: "blue" },
  contacted: { label: "Contacted", color: "yellow" },
  in_conversation: { label: "In Conversation", color: "purple" },
  qualified: { label: "Qualified", color: "green" },
  converted: { label: "Converted", color: "green" },
  deleted: { label: "Deleted", color: "grey" },
};

// --- Filter ---

type FilterType = "all" | LeadStatus;

const FILTER_OPTIONS: { key: FilterType; label: string }[] = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "in_conversation", label: "In Conversation" },
  { key: "qualified", label: "Qualified" },
  { key: "converted", label: "Converted" },
];

// --- Sorting ---

type SortKey = "companyName" | "contactName" | "status" | "createdAt";

// --- Component ---

interface LeadsTableProps {
  onSelectLead: (lead: Lead) => void;
}

const LeadsTable = ({
  onSelectLead,
}: LeadsTableProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filter, setFilter] = useState<FilterType>("all");
  const [sortBy, setSortBy] = useState<SortKey | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const skip = (currentPage - 1) * itemsPerPage;
  const statusFilter = filter === "all" ? undefined : filter;

  const { data, isLoading } = useLeads(
    debouncedSearchTerm || undefined,
    statusFilter,
    sortBy,
    sortOrder,
    skip,
    itemsPerPage,
  );

  const leads = data?.data ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (value: FilterType) => {
    setFilter(value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const getSortIcon = (key: SortKey) => {
    if (sortBy !== key) return <ChevronsUpDown className="ml-1 h-3 w-3" />;
    return sortOrder === "asc" ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  const showTableSkeleton = isLoading && leads.length === 0;

  return (
    <div className="space-y-4">
      {/* Search + filters — only when there are leads */}
      {totalItems > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#a4a4a4]" />
            <Input
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-10 text-sm border-[#f0f0f0] bg-[#fafafa] hover:bg-white focus-visible:bg-white transition-colors"
            />
          </div>
          <Select
            value={filter}
            onValueChange={(value: string) => handleFilterChange(value as FilterType)}
          >
            <SelectTrigger className="h-10 w-auto min-w-[100px] border-[#f0f0f0] shadow-none bg-[#fafafa] hover:bg-white px-3 rounded-lg text-xs font-medium text-[#494949] focus:ring-0 focus:border-[#f0f0f0] data-[state=open]:border-[#f0f0f0] transition-colors [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-[#a4a4a4]">
              {FILTER_OPTIONS.find((f) => f.key === filter)?.label || "All"}
            </SelectTrigger>
            <SelectContent align="end" className="min-w-[150px] border-[#f0f0f0] data-[state=open]:border-[#f0f0f0]">
              {FILTER_OPTIONS.map((f) => (
                <SelectItem key={f.key} value={f.key} className="text-xs font-medium">
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showTableSkeleton ? (
        <div className="space-y-3">
          {[...Array(itemsPerPage > 5 ? 5 : itemsPerPage)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : totalItems === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          {debouncedSearchTerm || filter !== "all" ? (
            <>
              <Search className="h-5 w-5 text-[#a4a4a4]" />
              <div className="mt-3 text-sm font-medium text-[#767677]">
                No matching leads
              </div>
              <div className="mt-1 text-xs text-[#a4a4a4]">
                Try adjusting your search or filters
              </div>
            </>
          ) : (
            <>
              <Users className="h-5 w-5 text-[#a4a4a4]" />
              <div className="mt-3 text-sm font-medium text-[#767677]">
                No leads yet
              </div>
              <div className="mt-1 text-xs text-[#a4a4a4]">
                Run your first search to discover leads
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Sort controls (desktop only) */}
          <div className="hidden sm:flex items-center px-4 pb-2 border-b border-[#f0f0f0]">
            <div className="flex-1 min-w-0">
              <button
                type="button"
                className="text-[10px] font-semibold uppercase tracking-wide text-[#a4a4a4] hover:text-[#767677] transition-colors inline-flex items-center"
                onClick={() => toggleSort("companyName")}
              >
                Company
                {getSortIcon("companyName")}
              </button>
            </div>
            <div className="flex-1 min-w-0 px-4">
              <button
                type="button"
                className="text-[10px] font-semibold uppercase tracking-wide text-[#a4a4a4] hover:text-[#767677] transition-colors inline-flex items-center"
                onClick={() => toggleSort("contactName")}
              >
                Contact
                {getSortIcon("contactName")}
              </button>
            </div>
            <div className="shrink-0 w-[120px] flex justify-end">
              <button
                type="button"
                className="text-[10px] font-semibold uppercase tracking-wide text-[#a4a4a4] hover:text-[#767677] transition-colors inline-flex items-center"
                onClick={() => toggleSort("status")}
              >
                Status
                {getSortIcon("status")}
              </button>
            </div>
          </div>

          {/* Lead rows */}
          <div className="flex flex-col">
            {leads.map((lead) => {
              const badge = STATUS_BADGE[lead.status];
              return (
                <div
                  key={lead.id}
                  className="px-4 py-3.5 cursor-pointer border-b border-[#f0f0f0] border-l-2 border-l-transparent hover:bg-[#fafafa] hover:border-l-sds-purple-100 transition-all group"
                  onClick={() => onSelectLead(lead)}
                >
                  {/* Desktop: single row with 3 columns */}
                  <div className="hidden sm:flex items-center">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#1e1e24] tracking-[-0.3px] truncate">
                        {lead.companyName}
                      </div>
                      <div className="text-[11px] text-[#a4a4a4] mt-0.5 truncate">
                        {lead.companyUrl.replace("https://", "")}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 px-4">
                      <div className="text-[13px] font-medium text-[#494949] tracking-[-0.3px] truncate">
                        {lead.contactName}
                      </div>
                      <div className="text-[11px] text-[#a4a4a4] mt-0.5 truncate">
                        {lead.contactTitle}
                      </div>
                    </div>
                    <div className="shrink-0 w-[120px] flex justify-end">
                      <Badge variant="opaque" color={badge.color} className="text-[11px]">
                        {badge.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Mobile: two-line card */}
                  <div className="sm:hidden space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-[#1e1e24] tracking-[-0.3px] truncate">
                          {lead.companyName}
                        </div>
                        <div className="text-[11px] text-[#a4a4a4] truncate">
                          {lead.companyUrl.replace("https://", "")}
                        </div>
                      </div>
                      <Badge variant="opaque" color={badge.color} className="text-[11px] shrink-0">
                        {badge.label}
                      </Badge>
                    </div>
                    <div className="text-[12px] text-[#767677] truncate">
                      {lead.contactName} · {lead.contactTitle}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination footer */}
          <div className="border-t px-4 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={handleItemsPerPageChange}
                >
                  <SelectTrigger className="h-8 w-[90px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">entries</span>
              </div>
              <span className="text-sm text-muted-foreground">
                Showing {skip + 1} to {Math.min(skip + itemsPerPage, totalItems)}{" "}
                of {totalItems} leads
              </span>
            </div>
            {totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      className={
                        currentPage === 1
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 &&
                        pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            onClick={() => setCurrentPage(pageNumber)}
                            isActive={currentPage === pageNumber}
                            className="cursor-pointer"
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }
                    return null;
                  })}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      className={
                        currentPage === totalPages
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LeadsTable;
